/**
 * Regole del modulo «Registra intervento» decise il 3/11/2026 (Osservazioni 6 di Carmem, decisioni di Davide):
 * la durata in ore e minuti, facoltativa, e la tendina «Progetto» in ordine alfabetico, per difetto con i soli
 * progetti della sede scelta.
 *
 * Senza React e senza dipendenze dal browser: le regole sono provate in
 * apps/api/test/intervento-osservazioni6.test.ts.
 */
import type { SedeElenco } from './sedi.ts'

// --- durata ------------------------------------------------------------------------------------------

/** I minuti che si scelgono nel modulo. Un valore registrato prima (per esempio 20) si mostra a parte. */
export const MINUTI_AMMESSI = [0, 15, 30, 45] as const

/** Le ore massime: la colonna e l'API arrivano a 24 ore (1440 minuti). */
export const ORE_MASSIME = 24

/** Ore e minuti come stanno nei due campi del modulo: testo, vuoto se non scritto. */
export type CampiDurata = { ore: string; minuti: string }

/**
 * I minuti da registrare: null se ore e minuti sono vuoti o fanno zero. Un campo vuoto accanto all'altro
 * scritto vale zero. NaN se le ore non sono un intero da 0 a 24: il modulo lo segnala prima di chiamare l'API.
 */
export function minutiDaCampi(c: CampiDurata): number | null {
  const ore = c.ore.trim()
  const minuti = c.minuti.trim()
  if (!ore && !minuti) return null
  const h = ore ? Number(ore) : 0
  const m = minuti ? Number(minuti) : 0
  if (!Number.isInteger(h) || h < 0 || h > ORE_MASSIME || !Number.isInteger(m) || m < 0 || m > 59) return Number.NaN
  const totale = h * 60 + m
  if (totale > ORE_MASSIME * 60) return Number.NaN
  return totale === 0 ? null : totale
}

/** I due campi da una durata registrata, senza conversioni: 78 minuti restano 1 ora e 18 minuti. */
export function campiDaMinuti(minuti: number | null | undefined): CampiDurata {
  if (minuti == null || minuti <= 0) return { ore: '', minuti: '' }
  return { ore: String(Math.floor(minuti / 60)), minuti: String(minuti % 60) }
}

/** Una durata da leggere in un elenco: «1 h 30 min», «45 min», «2 h»; il trattino se manca. */
export function durataBreve(minuti: number | null | undefined): string {
  if (minuti == null || minuti <= 0) return '—'
  const ore = Math.floor(minuti / 60)
  const resto = minuti % 60
  if (!ore) return `${resto} min`
  return resto ? `${ore} h ${resto} min` : `${ore} h`
}

// --- progetti ----------------------------------------------------------------------------------------

/** Un progetto come arriva da GET /progetti/selezionabili: `sedi` sono le sedi collegate (core.progetto_sede). */
export type ProgettoSelezionabile = { id: string; titolo: string; codice_cidis: string | null; sedi?: string[] }

const collatore = new Intl.Collator('it', { sensitivity: 'base', numeric: true })

/** Il titolo prima, il codice CIDIS fra parentesi: l'ordine alfabetico si legge sul titolo. */
export function etichettaProgetto(p: ProgettoSelezionabile): string {
  return p.codice_cidis ? `${p.titolo.trim()} (${p.codice_cidis})` : p.titolo.trim()
}

/** Per titolo, senza distinzione fra maiuscole e minuscole e fra lettere accentate e no, con le regole italiane. */
export function inOrdineAlfabetico<T extends ProgettoSelezionabile>(progetti: T[]): T[] {
  return [...progetti].sort((a, b) => collatore.compare(a.titolo.trim(), b.titolo.trim())
    || collatore.compare(a.codice_cidis ?? '', b.codice_cidis ?? '') || a.id.localeCompare(b.id))
}

/**
 * Le sedi che valgono come quella scelta: uno sportello vale come la sua sede madre, e la sede madre comprende
 * i suoi sportelli, come nel perimetro dei progetti di un incarico di sede (463, rotte/progetti.ts). Una sede
 * che non e' nell'elenco vale da sola.
 */
export function sediEquivalenti(sedeId: string, sedi: Pick<SedeElenco, 'id' | 'sede_padre_id'>[]): string[] {
  const sede = sedi.find((s) => s.id === sedeId)
  const madre = sede?.sede_padre_id ?? sedeId
  return [madre, ...sedi.filter((s) => s.sede_padre_id === madre).map((s) => s.id)]
    .filter((id, i, tutte) => tutte.indexOf(id) === i)
}

export type SceltaProgetti<T extends ProgettoSelezionabile> = {
  /** Le voci della tendina, in ordine alfabetico. */
  voci: T[]
  /** Quanti progetti ha la sede, e quanti ne restano fuori senza «Mostra tutti i progetti». */
  dellaSede: number
  fuori: number
  /** Il progetto gia' scelto non e' collegato alla sede: resta nella tendina, con una nota. */
  sceltoFuoriSede: boolean
}

/**
 * La tendina «Progetto»: senza sede o con `tutti` ogni progetto; altrimenti quelli della sede e delle sedi che
 * valgono come lei, piu' quello gia' scelto, che non sparisce cambiando sede. Una sede che non ha ancora progetti
 * collegati li propone tutti (decisione di Davide del 3/11/2026: meta' dei progetti non ha ancora una sede).
 */
export function progettiDaProporre<T extends ProgettoSelezionabile>(progetti: T[], o: {
  sedeId: string | null; sedi: Pick<SedeElenco, 'id' | 'sede_padre_id'>[]; tutti: boolean; scelto: string | null
}): SceltaProgetti<T> {
  const ordinati = inOrdineAlfabetico(progetti)
  if (!o.sedeId) return { voci: ordinati, dellaSede: ordinati.length, fuori: 0, sceltoFuoriSede: false }
  const valide = new Set(sediEquivalenti(o.sedeId, o.sedi))
  const dellaSede = (p: T) => (p.sedi ?? []).some((s) => valide.has(s))
  const quante = ordinati.filter(dellaSede).length
  const sceltoFuoriSede = quante > 0 && !!o.scelto && ordinati.some((p) => p.id === o.scelto && !dellaSede(p))
  return {
    voci: o.tutti || quante === 0 ? ordinati : ordinati.filter((p) => dellaSede(p) || p.id === o.scelto),
    dellaSede: quante,
    fuori: ordinati.length - quante,
    sceltoFuoriSede,
  }
}
